"use client"

import { buildCuratedEarnVaults } from "@src/features/earn/lib/buildCuratedEarnVaults"
import { useVaultDepositedBalances } from "@src/features/earn/lib/useVaultDepositedBalances"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { useMemo } from "react"

export type EarnVaultHolding = {
  defuseAssetId: string
  usdValue: number
}

export type EarnVaultBalance = {
  valueUsd: number
  /** Curated vaults with a non-zero on-chain share balance. */
  positionCount: number
  /** Held vaults with a known USD value (for blended APY). */
  holdings: EarnVaultHolding[]
  loading: boolean
  loadError: boolean
}

const EARN_VAULTS = buildCuratedEarnVaults()

export function useEarnVaultBalance(): EarnVaultBalance {
  const { state } = useConnectWallet()
  const userAddress = state.isAuthorized ? (state.address ?? null) : null
  const userChainType = state.chainType ?? null

  const { balances, isLoading: vaultBalancesLoading } =
    useVaultDepositedBalances(EARN_VAULTS, userAddress, userChainType, {
      isConfidential: true,
    })

  const summary = useMemo(() => {
    let total = 0
    let positionCount = 0
    const holdings: EarnVaultHolding[] = []
    let missingUsd = false

    for (const [defuseAssetId, entry] of balances) {
      if (!entry.hasBalance) continue
      positionCount += 1
      if (entry.usdValue == null) {
        missingUsd = true
        continue
      }
      total += entry.usdValue
      holdings.push({ defuseAssetId, usdValue: entry.usdValue })
    }

    return {
      valueUsd: missingUsd ? null : total,
      positionCount,
      holdings,
    }
  }, [balances])

  return {
    valueUsd: summary.valueUsd ?? 0,
    positionCount: summary.positionCount,
    holdings: summary.holdings,
    loading: vaultBalancesLoading,
    loadError: !vaultBalancesLoading && summary.valueUsd == null,
  }
}
